import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import PlanGate from "@/app/PlanGate";
import { PageHeader, Btn, EmptyState, Spinner, Money, StatusBadge } from "@/app/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Briefcase, Plus, MoreVertical, Play, CheckCircle2, FileText, Repeat, Calculator } from "lucide-react";
import { PhotoUploader, SignaturePad } from "@/app/components/MediaCapture";
import JobCosting from "@/app/components/JobCosting";
import { fmtDate, fmtDateInput, money } from "@/lib/format";

function JobsInner() {
  const { user } = useAuth();
  const sym = user?.business?.currency || "£";
  const isOwnerAdmin = user?.role === "owner" || user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const { data, loading, refetch } = useFetch("/jobs");
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [costingFor, setCostingFor] = useState(null);
  const [form, setForm] = useState({ title: "", customer_id: "", description: "", scheduled_date: fmtDateInput(new Date()), quoted_value: 0, address: "", recurring: "none" });
  const [reportFor, setReportFor] = useState(null);
  const [reportText, setReportText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [signature, setSignature] = useState(null);

  useEffect(() => { api.get("/customers?limit=200").then((r) => setCustomers(r.data.items)); }, []);
  useEffect(() => { if (params.get("new")) { setOpen(true); params.delete("new"); setParams(params, { replace: true }); } }, []); // eslint-disable-line

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try {
      await api.post("/jobs", { ...form, quoted_value: Number(form.quoted_value), scheduled_date: form.scheduled_date ? new Date(form.scheduled_date).toISOString() : null });
      toast.success("Job created"); setOpen(false); refetch();
      setForm({ title: "", customer_id: "", description: "", scheduled_date: fmtDateInput(new Date()), quoted_value: 0, address: "", recurring: "none" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, s) => { await api.post(`/jobs/${id}/status`, { status: s }); refetch(); };
  const openReport = (j) => { setReportFor(j); setReportText(j.report || ""); setPhotos(j.photos || []); setSignature(j.signature_file_id || null); };
  const saveReport = async () => {
    const { data: r } = await api.post(`/jobs/${reportFor.id}/report`, { report: reportText, photos, signature_file_id: signature });
    toast.success(r.next_recurrence ? `Completed — next ${reportFor.recurring} job scheduled` : "Report saved, job completed");
    setReportFor(null); setReportText(""); setPhotos([]); setSignature(null); refetch();
  };

  const items = data?.items || [];
  return (
    <div className="animate-fade-up">
      <PageHeader title="Jobs" description="Schedule work, track progress and capture sign-off."
        action={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="create-job-button"><Plus size={16} /> New job</Btn>} />
      {loading ? <Spinner /> : items.length === 0 ? (
        <EmptyState icon={Briefcase} title="No jobs yet" description="Schedule your first job and assign it to your team."
          action={<Btn variant="accent" onClick={() => setOpen(true)}><Plus size={16} /> New job</Btn>} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {items.map((j) => (
            <div key={j.id} className="amden-card p-5" data-testid={`job-card-${j.number}`}>
              <div className="flex items-start justify-between">
                <div><p className="font-mono text-xs text-slate-400">{j.number}</p><h3 className="font-semibold text-navy">{j.title}</h3></div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><button className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><MoreVertical size={16} /></button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setStatus(j.id, "in_progress")}><Play size={15} className="mr-2" /> Start job</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openReport(j)}><FileText size={15} className="mr-2" /> Complete & report</DropdownMenuItem>
                    {isOwnerAdmin && <DropdownMenuItem onClick={() => setCostingFor(j)} data-testid={`job-costing-open-${j.number}`}><Calculator size={15} className="mr-2" /> Costs & margin</DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <p className="text-sm text-slate-500 mt-1">{j.customer_name || "—"}</p>
              {j.recurring && j.recurring !== "none" && <span className="inline-flex items-center gap-1 mt-2 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-accent"><Repeat size={12} /> {j.recurring}</span>}
              {j.photos?.length > 0 && <span className="ml-2 text-xs text-slate-400">📷 {j.photos.length}</span>}
              {j.description && <p className="text-sm text-slate-400 mt-2 line-clamp-2">{j.description}</p>}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                <span className="text-xs text-slate-400">{fmtDate(j.scheduled_date)}</span>
                <div className="flex items-center gap-2"><Money value={j.quoted_value} symbol={sym} className="text-sm" /><StatusBadge status={j.status} /></div>
              </div>
              {isOwnerAdmin && typeof j.margin === "number" && j.revenue > 0 && (
                <div className="mt-2" data-testid={`job-margin-${j.number}`}>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${j.margin >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>Margin {money(j.margin, sym)} · {j.margin_pct}%</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New job</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><label className="text-sm font-medium text-slate-700">Title *</label><input value={form.title} onChange={set("title")} data-testid="job-field-title" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" /></div>
            <div><label className="text-sm font-medium text-slate-700">Customer</label><select value={form.customer_id} onChange={set("customer_id")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white"><option value="">None</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            <div><label className="text-sm font-medium text-slate-700">Description</label><textarea value={form.description} onChange={set("description")} rows={2} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700">Date</label><input type="date" value={form.scheduled_date} onChange={set("scheduled_date")} data-testid="job-field-date" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
              <div><label className="text-sm font-medium text-slate-700">Quoted value</label><input type="number" value={form.quoted_value} onChange={set("quoted_value")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono" /></div>
            </div>
            <div><label className="text-sm font-medium text-slate-700">Address</label><input value={form.address} onChange={set("address")} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
            <div><label className="text-sm font-medium text-slate-700">Repeat</label>
              <select value={form.recurring} onChange={set("recurring")} data-testid="job-field-recurring" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white">
                <option value="none">One-off job</option><option value="weekly">Every week</option><option value="monthly">Every month</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">Recurring jobs auto-schedule the next visit on the planner when completed.</p>
            </div>
          </div>
          <DialogFooter><Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={create} data-testid="job-save-button">Create job</Btn></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reportFor} onOpenChange={(o) => !o && setReportFor(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto amden-scroll">
          <DialogHeader><DialogTitle>Job report · {reportFor?.number}</DialogTitle></DialogHeader>
          <textarea value={reportText} onChange={(e) => setReportText(e.target.value)} rows={4} data-testid="job-report-text" placeholder="Describe the work completed…" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mt-2" />
          <div className="mt-3">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Site photos</label>
            <PhotoUploader photos={photos} onChange={setPhotos} />
          </div>
          <div className="mt-3">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Customer signature</label>
            <SignaturePad value={signature} onChange={setSignature} />
          </div>
          <DialogFooter><Btn variant="secondary" onClick={() => setReportFor(null)}>Cancel</Btn><Btn variant="accent" onClick={saveReport} data-testid="job-report-save"><CheckCircle2 size={16} /> Complete job</Btn></DialogFooter>
        </DialogContent>
      </Dialog>

      {costingFor && <JobCosting job={costingFor} sym={sym} onClose={() => setCostingFor(null)} onSaved={refetch} />}
    </div>
  );
}

export default function Jobs() { return <PlanGate feature="jobs"><JobsInner /></PlanGate>; }
